import { useState, useEffect } from 'react';
import { useAppStore } from '../store';
import {
    Cloud,
    PanelLeftClose,
    PanelLeft,
    Rocket,
    Settings,
    ExternalLink,
    Plus,
    FolderOpen,
    Check,
    ChevronDown,
    User,
    Key,
    Edit2,
    Trash2,
    Save,
    CheckCircle,
    Loader2,
    Square,
    CheckSquare
} from 'lucide-react';

export function Header() {
    const {
        project,
        projects,
        showSidebar,
        toggleSidebar,
        createProject,
        loadProject,
        updateProject,
        deleteProject,
        bulkDeleteProjects,
        fetchProjects,
        apiKey,
        setApiKey,
        isSyncing,
        setSyncing
    } = useAppStore();

    const [showProjectList, setShowProjectList] = useState(false);
    const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [tempKey, setTempKey] = useState(apiKey || '');
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

    useEffect(() => {
        fetchProjects();
    }, []);

    // Effect to handle visual save status
    useEffect(() => {
        if (isSyncing) {
            setSaveStatus('saving');
        } else if (saveStatus === 'saving') {
            setSaveStatus('saved');
            const timer = setTimeout(() => setSaveStatus('idle'), 2000);
            return () => clearTimeout(timer);
        }
    }, [isSyncing, saveStatus]);

    const handleDeploy = async () => {
        if (!project) return;
        try {
            const res = await fetch(`/api/deploy/${project.id}`, { method: 'POST' });
            const data = await res.json() as { success?: boolean; url?: string };
            if (data.success && data.url) {
                window.open(data.url, '_blank');
            }
        } catch (error) {
            console.error('Deploy failed:', error);
        }
    };

    const handleNewProject = () => {
        const name = prompt('Project Name:', 'My New Vibe');
        if (name) createProject(name);
    };

    return (
        <>
            <header className="h-14 flex items-center justify-between px-4 border-b border-border bg-background-secondary/80 backdrop-blur-md z-50">
                {/* Left section */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={toggleSidebar}
                        className="p-2 rounded-lg hover:bg-surface transition-colors"
                        aria-label={showSidebar ? 'Hide sidebar' : 'Show sidebar'}
                    >
                        {showSidebar ? (
                            <PanelLeftClose className="w-5 h-5 text-text-secondary" />
                        ) : (
                            <PanelLeft className="w-5 h-5 text-text-secondary" />
                        )}
                    </button>

                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center shadow-glow">
                            <Cloud className="w-4 h-4 text-white" />
                        </div>
                        <div className="relative">
                            <button
                                onClick={() => setShowProjectList(!showProjectList)}
                                className="flex flex-col items-start hover:bg-surface px-2 py-0.5 rounded transition-colors group"
                            >
                                <div className="flex items-center gap-1">
                                    <h1 className="font-bold text-text-primary text-sm tracking-tight">Vibeflare</h1>
                                    <ChevronDown className="w-3 h-3 text-text-muted group-hover:text-text-secondary" />
                                </div>
                                <p className="text-[10px] text-text-muted font-mono uppercase tracking-wider truncate max-w-[120px]">
                                    {project?.name || 'Create a project'}
                                </p>
                            </button>

                            {/* Save Status */}
                            {project && (
                                <div className="flex items-center gap-1.5 ml-2 text-[10px] font-medium transition-all duration-300">
                                    {saveStatus === 'saving' ? (
                                        <div className="flex items-center gap-1 text-accent-primary animate-pulse">
                                            <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                            <span>Saving...</span>
                                        </div>
                                    ) : saveStatus === 'saved' ? (
                                        <div className="flex items-center gap-1 text-accent-success">
                                            <CheckCircle className="w-2.5 h-2.5" />
                                            <span>Vibe Saved</span>
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            {showProjectList && (
                                <div className="absolute top-full left-0 mt-2 w-72 bg-surface border border-border rounded-xl shadow-2xl overflow-hidden animate-fade-in z-[60]">
                                    <div className="p-2 border-b border-border bg-background-secondary/50 flex items-center gap-2">
                                        <button
                                            onClick={() => { handleNewProject(); setShowProjectList(false); }}
                                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-accent-primary text-white text-xs font-semibold hover:opacity-90 transition-opacity"
                                        >
                                            <Plus className="w-4 h-4" />
                                            New Project
                                        </button>
                                        <button
                                            onClick={() => {
                                                setIsSelectionMode(!isSelectionMode);
                                                setSelectedProjects(new Set());
                                            }}
                                            className={`p-2 rounded-lg border transition-colors ${isSelectionMode ? 'bg-accent-primary/10 border-accent-primary text-accent-primary' : 'border-border text-text-muted hover:text-text-primary'}`}
                                            title="Manage projects"
                                        >
                                            <CheckSquare className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Bulk Actions Bar */}
                                    {isSelectionMode && selectedProjects.size > 0 && (
                                        <div className="p-2 bg-accent-error/5 border-b border-accent-error/20 flex items-center justify-between">
                                            <span className="text-[10px] font-bold text-accent-error uppercase tracking-wider ml-2">
                                                {selectedProjects.size} Selected
                                            </span>
                                            <button
                                                onClick={async () => {
                                                    if (confirm(`Delete ${selectedProjects.size} projects?`)) {
                                                        await bulkDeleteProjects(Array.from(selectedProjects));
                                                        setSelectedProjects(new Set());
                                                        setIsSelectionMode(false);
                                                    }
                                                }}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-error text-white text-[10px] font-bold hover:opacity-90 transition-opacity"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                                Bulk Delete
                                            </button>
                                        </div>
                                    )}

                                    <div className="max-h-64 overflow-y-auto p-1">
                                        {projects.length === 0 ? (
                                            <div className="p-4 text-center text-xs text-text-muted">No projects found</div>
                                        ) : (
                                            projects.map(p => (
                                                <div key={p.id} className="group/item flex items-center gap-1 p-1">
                                                    {isSelectionMode && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const newSet = new Set(selectedProjects);
                                                                if (newSet.has(p.id)) newSet.delete(p.id);
                                                                else newSet.add(p.id);
                                                                setSelectedProjects(newSet);
                                                            }}
                                                            className="p-1.5 rounded-lg transition-colors text-text-secondary"
                                                        >
                                                            {selectedProjects.has(p.id) ? (
                                                                <CheckSquare className="w-4 h-4 text-accent-primary" />
                                                            ) : (
                                                                <Square className="w-4 h-4 text-text-muted" />
                                                            )}
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            if (isSelectionMode) {
                                                                const newSet = new Set(selectedProjects);
                                                                if (newSet.has(p.id)) newSet.delete(p.id);
                                                                else newSet.add(p.id);
                                                                setSelectedProjects(newSet);
                                                            } else {
                                                                loadProject(p.id);
                                                                setShowProjectList(false);
                                                            }
                                                        }}
                                                        className={`flex-1 flex items-center justify-between px-3 py-2 rounded-lg text-xs hover:bg-background transition-colors ${p.id === project?.id ? 'text-accent-primary bg-accent-primary/5' : 'text-text-secondary'}`}
                                                    >
                                                        <div className="flex items-center gap-2 truncate">
                                                            <FolderOpen className="w-3.5 h-3.5" />
                                                            <span className="truncate">{p.name}</span>
                                                        </div>
                                                        {!isSelectionMode && p.id === project?.id && <Check className="w-3 h-3" />}
                                                    </button>
                                                    {!isSelectionMode && (
                                                        <>
                                                            <button
                                                                onClick={() => {
                                                                    const newName = prompt('Rename project:', p.name);
                                                                    if (newName && newName !== p.name) updateProject(p.id, { name: newName });
                                                                }}
                                                                className="opacity-0 group-hover/item:opacity-100 p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-text-primary transition-all"
                                                                title="Rename project"
                                                            >
                                                                <Edit2 className="w-3 h-3" />
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (confirm('Are you sure you want to delete this project?')) deleteProject(p.id);
                                                                }}
                                                                className="opacity-0 group-hover/item:opacity-100 p-1.5 hover:bg-accent-error/10 rounded-lg text-text-muted hover:text-accent-error transition-all"
                                                                title="Delete project"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right section */}
                <div className="flex items-center gap-2">
                    {/* Status indicator */}
                    {project?.status === 'deployed' && project.deployedUrl && (
                        <a
                            href={project.deployedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium
                           bg-accent-success/10 text-accent-success hover:bg-accent-success/20 transition-colors"
                        >
                            <span className="w-2 h-2 rounded-full bg-accent-success animate-pulse" />
                            Live
                            <ExternalLink className="w-3 h-3" />
                        </a>
                    )}

                    {/* Save button */}
                    <button
                        onClick={() => {
                            setSyncing(true);
                            setTimeout(() => setSyncing(false), 800);
                        }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                         border border-border hover:bg-surface transition-colors text-text-secondary"
                        title="Force Save Vibe"
                    >
                        <Save className="w-4 h-4" />
                        Save
                    </button>

                    {/* Deploy button */}
                    <button
                        onClick={handleDeploy}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                         bg-gradient-to-r from-accent-primary to-accent-secondary
                         hover:opacity-90 transition-opacity shadow-glow"
                    >
                        <Rocket className="w-4 h-4" />
                        Deploy
                    </button>

                    {/* Settings */}
                    <button
                        onClick={() => setShowSettings(true)}
                        className="p-2 rounded-lg hover:bg-surface transition-colors"
                    >
                        <Settings className="w-5 h-5 text-text-secondary" />
                    </button>
                </div>
            </header>

            {/* Settings Modal */}
            {showSettings && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-surface border border-border w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
                        <div className="p-6 border-b border-border flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-accent-primary/10 flex items-center justify-center">
                                    <User className="w-5 h-5 text-accent-primary" />
                                </div>
                                <div>
                                    <h2 className="font-bold text-text-primary">Settings</h2>
                                    <p className="text-xs text-text-muted">Configure your vibe environment</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowSettings(false)}
                                className="p-2 hover:bg-background rounded-lg text-text-muted hover:text-text-primary transition-colors"
                            >
                                <PanelLeftClose className="w-5 h-5 rotate-180" />
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm font-medium text-text-secondary">
                                    <Key className="w-4 h-4 text-accent-primary" />
                                    AI API Key (Custom)
                                </label>
                                <input
                                    type="password"
                                    value={tempKey}
                                    onChange={(e) => setTempKey(e.target.value)}
                                    placeholder="Enter your API key..."
                                    className="w-full px-4 py-2.5 rounded-xl bg-background border border-border focus:border-accent-primary focus:ring-1 focus:ring-accent-primary outline-none text-sm transition-all"
                                />
                                <p className="text-[10px] text-text-muted leading-relaxed">
                                    Optional. If provided, we'll use your key for generations. Keys are stored locally in your browser.
                                </p>
                            </div>
                        </div>
                        <div className="p-6 bg-background-secondary/50 flex gap-3">
                            <button
                                onClick={() => setShowSettings(false)}
                                className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-surface transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => { setApiKey(tempKey); setShowSettings(false); }}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-accent-primary text-white text-sm font-bold hover:opacity-90 transition-opacity"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
